import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Briefcase, Loader2, Eye, Bot, Pencil, Filter, Search,
  ChevronLeft, ChevronRight, X, Trash2, Check, Pause, Play,
} from "lucide-react";
import { WorkModelBadge } from "@/components/jobs/WorkModelBadge";
import { DeadlineBadge } from "@/components/jobs/DeadlineBadge";
import { SourceBadge } from "@/components/jobs/SourceBadge";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  isCrawlerJob as checkCrawler,
  getCompanyName,
} from "@/lib/jobUtils";
import { JOB_STATUS_LABELS } from "@/lib/constants";
import { JobQualityBadge } from "@/components/admin/JobQualityBadge";
import { getJobQuality } from "@/lib/jobQuality";
import { useJobLocations } from "@/hooks/useJobLocations";
import { CityCombobox } from "@/components/admin/CityCombobox";

const statusLabels = JOB_STATUS_LABELS;
const PAGE_SIZE_OPTIONS = [20, 50, 100, 500] as const;

type StatusFilter = "all" | "publicada" | "pausada" | "em_revisao" | "rascunho" | "pending_review" | "descartada" | "encerrada";

export default function AdminTodasVagas() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [orgFilter, setOrgFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmAction, setConfirmAction] = useState<{ type: "delete" | "status"; ids: string[]; status?: string; label?: string } | null>(null);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string | null>(null);
  const [qualityFilter, setQualityFilter] = useState<string>("all");

  const { states: availableStates, cities: availableCities } = useJobLocations({
    selectedState: stateFilter,
  });

  // --- Mutual exclusion handlers: search vs geo filters ---
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.trim() && (stateFilter || cityFilter)) {
      setStateFilter(null);
      setCityFilter(null);
      toast({ title: "Filtros de localidade removidos", description: "A busca pesquisa em todos os campos." });
    }
  };
  const handleStateChange = (value: string) => {
    setStateFilter(value === "all" ? null : value);
    setCityFilter(null);
    if (searchTerm.trim()) {
      setSearchTerm("");
      setDebouncedSearch("");
      toast({ title: "Busca removida", description: "Filtros de localidade aplicados." });
    }
    setPage(0);
  };
  const handleCityChange = (value: string | null) => {
    setCityFilter(value);
    if (searchTerm.trim()) {
      setSearchTerm("");
      setDebouncedSearch("");
      toast({ title: "Busca removida", description: "Filtros de localidade aplicados." });
    }
    setPage(0);
  };

  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 350);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchTerm]);

  // Clear selection on filter/page changes
  useEffect(() => {
    setSelected(new Set());
  }, [page, statusFilter, orgFilter, debouncedSearch, stateFilter, cityFilter, qualityFilter]);

  const isSearching = debouncedSearch.trim().length > 0;
  const isQualityFiltering = qualityFilter !== "all";

  const { data: queryResult, isLoading, isFetching } = useQuery({
    queryKey: ["admin_all_jobs", statusFilter, orgFilter, debouncedSearch, page, pageSize, stateFilter, cityFilter, qualityFilter],
    queryFn: async () => {
      if (isSearching) {
        const { data, error } = await (supabase.rpc as any)("search_review_jobs", {
          search_term: debouncedSearch.trim(),
          status_filter: statusFilter,
          org_filter: orgFilter === "all" ? null : orgFilter,
          source_filter: "all",
          result_limit: isQualityFiltering ? 500 : pageSize,
          result_offset: isQualityFiltering ? 0 : page * pageSize,
        });
        if (error) throw error;
        const rows = (data ?? []) as any[];
        const totalCount = rows.length > 0 ? Number(rows[0].total_count) : 0;
        return {
          jobs: rows.map((r: any) => ({
            id: r.id, title: r.title, employment_type: r.employment_type,
            work_model: r.work_model, status: r.status, created_at: r.created_at,
            org_id: r.org_id, description: r.description, description_summary: r.description_summary,
            location_city: r.location_city, location_state: r.location_state,
            deadline_date: r.deadline_date, skills_tags: r.skills_tags,
            partner_orgs: {
              name: r.org_name, is_auto_created: r.org_is_auto_created,
              source_origin: r.org_source_origin, logo_url: r.org_logo_url, industry: r.org_industry,
            },
            crawler_metadata: r.crawler_source_name ? [{
              company_name: r.crawler_company_name, source_url: r.crawler_source_url,
              source_name: r.crawler_source_name, source_type: r.crawler_source_type,
              external_apply_url: r.crawler_external_apply_url, crawler_score: r.crawler_score,
            }] : [],
          })),
          totalCount,
        };
      }

      const fetchLimit = isQualityFiltering ? 500 : pageSize;
      const fetchOffset = isQualityFiltering ? 0 : page * pageSize;
      let query = supabase
        .from("job_posts")
        .select("*, partner_orgs(name, is_auto_created, source_origin, logo_url, industry), crawler_metadata(source_url, source_name, source_type, external_apply_url, company_name, crawler_score)", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(fetchOffset, fetchOffset + fetchLimit - 1);

      if (statusFilter !== "all") query = query.eq("status", statusFilter);
      if (orgFilter !== "all") query = query.eq("org_id", orgFilter);
      if (stateFilter) query = query.eq("location_state", stateFilter);
      if (cityFilter) query = query.eq("location_city", cityFilter);

      const { data, error, count } = await query;
      if (error) throw error;
      return { jobs: data ?? [], totalCount: count ?? 0 };
    },
    placeholderData: (prev) => prev,
  });

  // Hybrid pagination: quality filter applied client-side
  const { jobs, totalCount, totalPages } = useMemo(() => {
    const raw = queryResult?.jobs ?? [];
    const serverTotal = queryResult?.totalCount ?? 0;
    if (!isQualityFiltering) {
      return { jobs: raw, totalCount: serverTotal, totalPages: Math.max(1, Math.ceil(serverTotal / pageSize)) };
    }
    const qualityFiltered = raw.filter((j: any) => getJobQuality(j).tier === qualityFilter);
    const sliced = qualityFiltered.slice(page * pageSize, (page + 1) * pageSize);
    return {
      jobs: sliced,
      totalCount: qualityFiltered.length,
      totalPages: Math.max(1, Math.ceil(qualityFiltered.length / pageSize)),
    };
  }, [queryResult, qualityFilter, isQualityFiltering, page, pageSize]);

  // Batch status update
  const batchUpdate = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      const update: any = { status };
      if (status === "publicada") update.published_at = new Date().toISOString();
      const CHUNK = 200;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const { error } = await supabase.from("job_posts").update(update).in("id", chunk);
        if (error) throw error;
      }
    },
    onSuccess: (_, { ids, status }) => {
      queryClient.invalidateQueries({ queryKey: ["admin_all_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin_review_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin_active_jobs"] });
      setSelected(new Set());
      const msgs: Record<string, string> = {
        publicada: "publicada(s)", pausada: "pausada(s)", descartada: "descartada(s)",
        em_revisao: "movida(s) para revisão", encerrada: "encerrada(s)",
      };
      toast({ title: `${ids.length} vaga(s) ${msgs[status] ?? "atualizada(s)"}` });
    },
    onError: (err) => {
      toast({ title: "Erro ao atualizar", description: (err as Error).message, variant: "destructive" });
    },
  });

  // Batch delete
  const batchDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      const CHUNK = 50;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        await supabase.from("crawler_metadata").delete().in("job_post_id", chunk);
        await supabase.from("job_applications").delete().in("job_id", chunk);
        const { error } = await supabase.from("job_posts").delete().in("id", chunk);
        if (error) throw error;
      }
    },
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ["admin_all_jobs"] });
      setSelected(new Set());
      toast({ title: `${ids.length} vaga(s) excluída(s) permanentemente` });
    },
    onError: (err) => {
      toast({ title: "Erro ao excluir", description: (err as Error).message, variant: "destructive" });
    },
  });

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllPage = useCallback(() => {
    const pageIds = jobs.map((j) => j.id);
    setSelected((prev) => {
      const allSelected = pageIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...pageIds]);
    });
  }, [jobs]);

  const selectAllFiltered = useCallback(async () => {
    const MAX = 500;
    let q = supabase.from("job_posts").select("id");
    if (statusFilter !== "all") q = q.eq("status", statusFilter);
    if (orgFilter !== "all") q = q.eq("org_id", orgFilter);
    const { data } = await q.range(0, MAX - 1);
    const ids = (data ?? []).map((d) => d.id);
    setSelected((prev) => {
      if (ids.every((id) => prev.has(id))) return new Set();
      return new Set(ids);
    });
  }, [statusFilter, orgFilter]);

  const { data: jobOrgs } = useQuery({
    queryKey: ["admin_job_orgs"],
    queryFn: async () => {
      const { data, error } = await supabase.from("partner_orgs").select("id, name").order("name");
      if (error) throw error;
      return (data ?? []).map((o) => [o.id, o.name] as [string, string]);
    },
    staleTime: 5 * 60 * 1000,
  });

  const statusFilterOptions: { key: StatusFilter; label: string }[] = [
    { key: "all", label: "Todos os status" },
    { key: "publicada", label: "Publicada" },
    { key: "pausada", label: "Pausada" },
    { key: "em_revisao", label: "Em revisão" },
    { key: "pending_review", label: "Enriquecendo" },
    { key: "rascunho", label: "Rascunho" },
    { key: "descartada", label: "Descartada" },
    { key: "encerrada", label: "Encerrada" },
  ];

  if (isLoading) {
    return <div className="p-8 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const pageIds = jobs.map((j) => j.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  return (
    <div className="p-8 max-w-6xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-2xl font-bold text-foreground">Todas as Vagas</h1>
        <p className="text-muted-foreground text-sm mt-1 flex items-center gap-2">
          Visualize e gerencie todas as vagas do sistema · {totalCount} total
          {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        </p>
      </motion.div>

      {/* Filters */}
      <div className="flex gap-3 mt-4 flex-wrap items-center">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Filtros:
        </div>
        <Select value={orgFilter} onValueChange={(v) => { setOrgFilter(v); setPage(0); }}>
          <SelectTrigger className="w-52 h-8 text-xs"><SelectValue placeholder="Organização" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as organizações</SelectItem>
            {(jobOrgs ?? []).map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as StatusFilter); setPage(0); }}>
          <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {statusFilterOptions.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={stateFilter ?? "all"} onValueChange={handleStateChange}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estados</SelectItem>
            {availableStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <CityCombobox cities={availableCities} value={cityFilter} onChange={handleCityChange} />
        <Select value={qualityFilter} onValueChange={(v) => { setQualityFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualquer qualidade</SelectItem>
            <SelectItem value="baixa">Baixa</SelectItem>
            <SelectItem value="media">Média</SelectItem>
            <SelectItem value="alta">Alta</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative ml-auto w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por título, empresa, cidade..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-8 pr-7 h-8 text-xs"
          />
          {searchTerm && (
            <button onClick={() => { setSearchTerm(""); setDebouncedSearch(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Batch action bar */}
      {someSelected && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl p-3 flex-wrap"
        >
          <span className="text-sm font-medium text-foreground">{selected.size} selecionada(s)</span>
          <Button size="sm" onClick={() => setConfirmAction({ type: "status", ids: [...selected], status: "publicada", label: "Publicar" })} disabled={batchUpdate.isPending}>
            <Check className="w-4 h-4 mr-1" /> Publicar
          </Button>
          <Button size="sm" variant="outline" onClick={() => setConfirmAction({ type: "status", ids: [...selected], status: "pausada", label: "Pausar" })} disabled={batchUpdate.isPending}>
            <Pause className="w-4 h-4 mr-1" /> Pausar
          </Button>
          <Button size="sm" variant="outline" onClick={() => setConfirmAction({ type: "status", ids: [...selected], status: "descartada", label: "Descartar" })} disabled={batchUpdate.isPending}>
            <X className="w-4 h-4 mr-1" /> Descartar
          </Button>
          <Button size="sm" variant="destructive" onClick={() => setConfirmAction({ type: "delete", ids: [...selected] })} disabled={batchDelete.isPending}>
            <Trash2 className="w-4 h-4 mr-1" /> Excluir
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Limpar seleção</Button>
        </motion.div>
      )}

      {/* Selection controls + page size */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Checkbox checked={allPageSelected} onCheckedChange={selectAllPage} />
        <span className="text-xs text-muted-foreground">Selecionar página ({jobs.length})</span>
        <button onClick={selectAllFiltered} className="text-xs text-primary hover:underline">
          {selected.size >= Math.min(totalCount, 500) && totalCount > 0 ? "Desmarcar todas" : `Selecionar todas (${Math.min(totalCount, 500)}) · máx. 500`}
        </button>
        <div className="flex-1" />
        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
          <SelectTrigger className="h-8 w-[100px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>{size} / pág</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Jobs list */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-4">
        <div className="space-y-2">
          {jobs.map((job) => {
            const sl = statusLabels[job.status] ?? { label: job.status, variant: "outline" as const };
            const isCrawlerJob = checkCrawler(job);
            const isSelected = selected.has(job.id);
            return (
              <div key={job.id} className={`bg-card rounded-xl p-3 border transition-colors ${isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(job.id)} />
                    <div className="min-w-0 cursor-pointer flex-1" onClick={() => setSelectedJob(job)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-foreground text-sm truncate">{job.title}</h3>
                        {isCrawlerJob && <Bot className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                        <JobQualityBadge job={job} />
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap text-xs text-muted-foreground">
                        <span>{getCompanyName(job)} · {job.employment_type}</span>
                        <WorkModelBadge model={job.work_model} size="sm" />
                        {job.location_city && <span>· {job.location_city}</span>}
                        <DeadlineBadge deadline={job.deadline_date} size="sm" />
                        {checkCrawler(job) && (() => {
                          const meta = (job as any).crawler_metadata?.[0];
                          return meta?.source_name ? <SourceBadge sourceName={meta.source_name} sourceType={meta.source_type} size="sm" /> : null;
                        })()}
                      </div>
                      {job.description_summary && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{job.description_summary}</p>
                      )}
                      {Array.isArray(job.skills_tags) && job.skills_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(job.skills_tags as string[]).slice(0, 4).map((tag) => (
                            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{tag}</span>
                          ))}
                          {job.skills_tags.length > 4 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">+{job.skills_tags.length - 4}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant={sl.variant}>{sl.label}</Badge>
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setSelectedJob(job)} title="Ver detalhes">
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => navigate(`/admin/todas-vagas/${job.id}/editar`)} title="Editar">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          {jobs.length === 0 && (
            <div className="bg-muted/50 rounded-2xl p-8 text-center">
              <Briefcase className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Nenhuma vaga encontrada</p>
            </div>
          )}
        </div>
      </motion.section>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Página {page + 1} de {totalPages} · {totalCount} vaga(s)
          </p>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const startPage = Math.max(0, Math.min(page - 2, totalPages - 5));
              const p = startPage + i;
              return (
                <Button key={p} size="sm" variant={p === page ? "default" : "ghost"} className="h-7 w-7 px-0 text-xs" onClick={() => setPage(p)}>
                  {p + 1}
                </Button>
              );
            })}
            <Button size="sm" variant="ghost" className="h-7 px-2" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Job Detail Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={(open) => !open && setSelectedJob(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedJob && (() => {
            const meta = (selectedJob as any).crawler_metadata?.[0];
            const isCrawlerJob = checkCrawler(selectedJob);
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 flex-wrap">
                    <DialogTitle className="text-xl">{selectedJob.title}</DialogTitle>
                    {isCrawlerJob && (
                      <Badge variant="secondary" className="text-xs flex items-center gap-1">
                        <Bot className="w-3 h-3" /> Crawler
                      </Badge>
                    )}
                    <JobQualityBadge job={selectedJob} />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                    <span>{getCompanyName(selectedJob)} · {selectedJob.employment_type}</span>
                    <WorkModelBadge model={selectedJob.work_model} size="sm" />
                    <DeadlineBadge deadline={selectedJob.deadline_date} size="sm" />
                  </div>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                  {meta && (
                    <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-2"><Bot className="w-4 h-4" /> Origem</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Fonte:</span> <SourceBadge sourceName={meta.source_name} sourceType={meta.source_type} size="sm" /></div>
                        {meta.company_name && <div><span className="text-muted-foreground">Empresa:</span> <span className="font-medium">{meta.company_name}</span></div>}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Status:</span>
                    <Badge variant={statusLabels[selectedJob.status]?.variant ?? "outline"}>
                      {statusLabels[selectedJob.status]?.label ?? selectedJob.status}
                    </Badge>
                  </div>

                  {selectedJob.location_city && (
                    <div>
                      <span className="text-sm font-medium text-foreground">Local: </span>
                      <span className="text-sm text-muted-foreground">{selectedJob.location_city}{selectedJob.location_state ? ` - ${selectedJob.location_state}` : ""}</span>
                    </div>
                  )}

                  {selectedJob.description && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">Descrição</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6">{selectedJob.description}</p>
                    </div>
                  )}

                  <div className="flex gap-2 pt-4 border-t border-border flex-wrap">
                    <Button variant="outline" onClick={() => { setSelectedJob(null); navigate(`/admin/todas-vagas/${selectedJob.id}/editar`); }}>
                      <Pencil className="w-4 h-4 mr-1" /> Editar
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Confirm batch action */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "delete"
                ? `Excluir ${confirmAction.ids.length} vaga(s) permanentemente?`
                : `${confirmAction?.label} ${confirmAction?.ids.length} vaga(s)?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "delete"
                ? "Esta ação não pode ser desfeita. As vagas e seus dados relacionados serão removidos permanentemente."
                : `As vagas selecionadas terão seu status alterado para "${confirmAction?.label}".`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className={confirmAction?.type === "delete" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === "delete") {
                  batchDelete.mutate(confirmAction.ids);
                } else if (confirmAction.status) {
                  batchUpdate.mutate({ ids: confirmAction.ids, status: confirmAction.status });
                }
                setConfirmAction(null);
              }}
            >
              {confirmAction?.type === "delete" ? "Excluir permanentemente" : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
